import { useLocalSearchParams } from 'expo-router'
import {
    AlertTriangle,
    Calendar,
    KeyRound,
    Mail,
    MapPin,
    Palette,
    RotateCcw,
    Shield,
    User,
    UserCog,
    UserX,
} from 'lucide-react-native'
import { useEffect, useMemo, useState } from 'react'
import type { Control, UseFormSetValue, UseFormWatch } from 'react-hook-form'
import { StyleSheet, View } from 'react-native'
import { Card, Divider, Switch, Text, TouchableRipple, useTheme } from 'react-native-paper'
import { TrackingPauseSection } from '@/components/TrackingPauseSection'
import { UnsavedChangesDialog } from '@/components/UnsavedChangesDialog'
import { WorkingHoursForm } from '@/components/WorkingHoursForm'
import { USER_COLORS } from '@/constants/monitor'
import { useSemanticColors } from '@/constants/SemanticColors'
import { useMemberPermissions, useMembers } from '@/hooks/people'
import { useCurrentOrg } from '@/hooks/useCurrentOrg'
import { useHeaderOptions } from '@/hooks/useHeaderOptions'
import { useNavigateBack } from '@/hooks/useNavigateBack'
import { formatLastActivity } from '@/hooks/utils'
import { useAuth } from '@/lib/auth'
import { PB_SERVER_ADDR } from '@/lib/config'
import { captureException, errorToString, handleMutationErrorsWithForm } from '@/lib/errors'
import { performMutations, useMutation } from '@/lib/mutations'
import { notify } from '@/lib/notice'
import { pb, useStore } from '@/lib/pocketbase'
import { isPauseDateWithinLimit } from '@/lib/tracking-pause'
import { isValidTimeRange } from '@/lib/working-hours'
import type { Member, MembersData, UserRole } from '@/types/people'
import { DEFAULT_WORKING_HOURS } from '@/types/workingHours'
import { Button } from '@/ui/Button'
import { SelectInput, TextInput, useForm, z, zodResolver } from '@/ui/form'
import { LoadingIndicator } from '@/ui/LoadingIndicator'
import { Page } from '@/ui/Page'

const ROLE_OPTIONS: { value: UserRole; label: string; description: string }[] = [
    {
        value: 'admin',
        label: 'Admin',
        description: 'Can manage members and access most organization features except billing.',
    },
    {
        value: 'clerical',
        label: 'Clerical',
        description:
            'Standard access to organization features with limited administrative capabilities.',
    },
    {
        value: 'workforce',
        label: 'Workforce',
        description: 'Restricted access with minimal permissions.',
    },
]

const TIME_REGEX = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/

const memberEditSchema = z
    .object({
        name: z.string(),
        email: z.string().email('Please enter a valid email address'),
        role: z.enum(['admin', 'clerical', 'workforce']),
        color: z
            .string()
            .regex(/^#[0-9A-Fa-f]{6}$/)
            .optional()
            .or(z.literal('')),
        geo_tracking_disabled: z.boolean(),
        working_hours_override: z.boolean(),
        work_start_time: z.string().regex(TIME_REGEX),
        work_end_time: z.string().regex(TIME_REGEX),
        working_days: z.array(z.number().min(0).max(6)),
        tracking_paused_until: z.string().nullable().optional(),
        supervisor: z.string().optional().or(z.literal('')),
        clockin_reminders_disabled: z.boolean(),
    })
    .refine(
        data => {
            if (!data.working_hours_override) return true
            return isValidTimeRange(data.work_start_time, data.work_end_time)
        },
        {
            message: 'Start time must be before end time',
            path: ['work_end_time'],
        }
    )
    .refine(data => isPauseDateWithinLimit(data.tracking_paused_until), {
        message: 'Tracking can only be paused for up to one month',
        path: ['tracking_paused_until'],
    })

type MemberEditFormData = z.infer<typeof memberEditSchema>

function ColorPickerSection({
    value,
    onChange,
}: {
    value: string
    onChange: (color: string) => void
}) {
    const theme = useTheme()

    return (
        <View style={styles.detailSection}>
            <View style={styles.detailHeader}>
                <Palette size={16} color={theme.colors.onSurfaceVariant} />
                <Text
                    variant="titleSmall"
                    style={[styles.detailLabel, { color: theme.colors.onSurface }]}
                >
                    Identifying Color
                </Text>
            </View>
            <View style={styles.colorSwatchGrid}>
                {USER_COLORS.map(color => {
                    const isSelected = value === color
                    return (
                        <TouchableRipple
                            key={color}
                            onPress={() => onChange(isSelected ? '' : color)}
                            style={[
                                styles.colorSwatch,
                                { backgroundColor: color },
                                isSelected && {
                                    borderWidth: 3,
                                    borderColor: theme.colors.onSurface,
                                },
                            ]}
                            borderless
                        >
                            <View />
                        </TouchableRipple>
                    )
                })}
            </View>
            <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>
                {value
                    ? 'This color will be used to identify this member'
                    : 'No color set — a color will be auto-assigned'}
            </Text>
        </View>
    )
}

function LocationTrackingCard({
    control,
    watch,
    setValue,
    canEdit,
    isPending,
}: {
    control: Control<MemberEditFormData>
    watch: UseFormWatch<MemberEditFormData>
    setValue: UseFormSetValue<MemberEditFormData>
    canEdit: boolean
    isPending: boolean
}) {
    const theme = useTheme()
    const formValues = watch()

    return (
        <Card style={styles.locationCard}>
            <Card.Content style={styles.cardContent}>
                <View style={styles.sectionHeader}>
                    <MapPin size={18} color={theme.colors.onSurfaceVariant} />
                    <Text variant="titleLarge" style={styles.sectionTitle}>
                        Location Tracking
                    </Text>
                </View>

                <View style={styles.detailSection}>
                    <View style={styles.toggleRow}>
                        <View style={styles.toggleInfo}>
                            <Text variant="titleSmall">Enable location tracking</Text>
                            <Text
                                variant="bodySmall"
                                style={{ color: theme.colors.onSurfaceVariant }}
                            >
                                Track this user's location for automatic time entries
                            </Text>
                        </View>
                        <Switch
                            value={!formValues.geo_tracking_disabled}
                            onValueChange={value => {
                                setValue('geo_tracking_disabled', !value, { shouldDirty: true })
                            }}
                            disabled={!canEdit || isPending}
                        />
                    </View>

                    {!formValues.geo_tracking_disabled && (
                        <>
                            <Divider style={styles.divider} />
                            <TrackingPauseSection
                                pausedUntil={formValues.tracking_paused_until ?? null}
                                onSetPause={date =>
                                    setValue('tracking_paused_until', date, {
                                        shouldDirty: true,
                                    })
                                }
                                disabled={!canEdit || isPending}
                            />
                            <Divider style={styles.divider} />
                            <WorkingHoursForm
                                control={control}
                                watch={watch}
                                setValue={setValue}
                                enabledFieldName="working_hours_override"
                                startTimeFieldName="work_start_time"
                                endTimeFieldName="work_end_time"
                                workingDaysFieldName="working_days"
                                disabled={!canEdit || isPending}
                                title="Custom Working Hours"
                                description="Set custom working hours for this team member to override organization defaults."
                            />
                        </>
                    )}
                </View>
            </Card.Content>
        </Card>
    )
}

function AdminActionsCard({
    isVisible,
    isInactive,
    isOrgInactive,
    onReactivate,
    onSendPasswordReset,
    onToggleOrgActive,
    isReactivating,
    isSendingReset,
    isTogglingActive,
    inactiveAt,
}: {
    isVisible: boolean
    isInactive: boolean
    isOrgInactive: boolean
    onReactivate: () => void
    onSendPasswordReset: () => void
    onToggleOrgActive: () => void
    isReactivating: boolean
    isSendingReset: boolean
    isTogglingActive: boolean
    inactiveAt: string | null
}) {
    const theme = useTheme()

    if (!isVisible) return null

    return (
        <Card style={styles.actionsCard}>
            <Card.Content style={styles.cardContent}>
                <Text variant="titleLarge" style={styles.sectionTitle}>
                    Admin Actions
                </Text>

                <View style={styles.actionItem}>
                    <View style={styles.actionInfo}>
                        <UserX
                            size={20}
                            color={isOrgInactive ? theme.colors.error : theme.colors.primary}
                        />
                        <View style={styles.actionText}>
                            <Text variant="titleSmall">
                                {isOrgInactive
                                    ? 'Disabled in Organization'
                                    : 'Disable in Organization'}
                            </Text>
                            <Text
                                variant="bodySmall"
                                style={{ color: theme.colors.onSurfaceVariant }}
                            >
                                {isOrgInactive
                                    ? 'This user cannot access this organization'
                                    : 'Prevent this user from accessing this organization'}
                            </Text>
                            {isOrgInactive && inactiveAt && (
                                <Text
                                    variant="bodySmall"
                                    style={{ color: theme.colors.onSurfaceVariant }}
                                >
                                    Disabled on: {new Date(inactiveAt).toLocaleDateString()}
                                </Text>
                            )}
                        </View>
                    </View>
                    <Switch
                        value={isOrgInactive}
                        onValueChange={onToggleOrgActive}
                        disabled={isTogglingActive}
                    />
                </View>

                {isInactive && (
                    <View style={styles.actionItem}>
                        <View style={styles.actionInfo}>
                            <RotateCcw size={20} color={theme.colors.primary} />
                            <View style={styles.actionText}>
                                <Text variant="titleSmall">Reactivate Account</Text>
                                <Text
                                    variant="bodySmall"
                                    style={{ color: theme.colors.onSurfaceVariant }}
                                >
                                    Allow this user to sign in again
                                </Text>
                            </View>
                        </View>
                        <Button
                            title="Reactivate"
                            onPress={onReactivate}
                            loading={isReactivating}
                            loadingTitle="..."
                            mode="contained"
                        />
                    </View>
                )}

                <View style={styles.actionItem}>
                    <View style={styles.actionInfo}>
                        <KeyRound size={20} color={theme.colors.primary} />
                        <View style={styles.actionText}>
                            <Text variant="titleSmall">Send Password Reset</Text>
                            <Text
                                variant="bodySmall"
                                style={{ color: theme.colors.onSurfaceVariant }}
                            >
                                Send a password reset email to this user
                            </Text>
                        </View>
                    </View>
                    <Button
                        title="Send"
                        onPress={onSendPasswordReset}
                        loading={isSendingReset}
                        loadingTitle="..."
                        mode="outlined"
                        disabled={isInactive}
                    />
                </View>
            </Card.Content>
        </Card>
    )
}

function SupervisorCard({
    isVisible,
    control,
    members,
    watch,
    setValue,
}: {
    isVisible: boolean
    control: Control<MemberEditFormData>
    members: MembersData
    watch: UseFormWatch<MemberEditFormData>
    setValue: UseFormSetValue<MemberEditFormData>
}) {
    const theme = useTheme()
    const formValues = watch()

    if (!isVisible) return null

    const supervisorOptions = members.members
        .filter(m => m.role === 'admin' || m.role === 'clerical')
        .map(m => ({
            value: m.user.id,
            label: m.user.name || m.user.email,
        }))

    return (
        <Card style={styles.locationCard}>
            <Card.Content style={styles.cardContent}>
                <View style={styles.sectionHeader}>
                    <UserCog size={18} color={theme.colors.onSurfaceVariant} />
                    <Text variant="titleLarge" style={styles.sectionTitle}>
                        Clock-In Reminders
                    </Text>
                </View>

                <View style={styles.detailSection}>
                    <View style={styles.toggleRow}>
                        <View style={styles.toggleInfo}>
                            <Text variant="titleSmall">Exempt from reminders</Text>
                            <Text
                                variant="bodySmall"
                                style={{ color: theme.colors.onSurfaceVariant }}
                            >
                                This user will not receive clock-in reminder notifications
                            </Text>
                        </View>
                        <Switch
                            value={formValues.clockin_reminders_disabled}
                            onValueChange={value =>
                                setValue('clockin_reminders_disabled', value, {
                                    shouldDirty: true,
                                })
                            }
                        />
                    </View>
                </View>

                <Divider style={styles.divider} />

                <View style={styles.detailSection}>
                    <Text variant="titleSmall">Supervisor</Text>
                    <SelectInput<MemberEditFormData>
                        name="supervisor"
                        horizontal
                        control={control}
                        label=""
                        options={[{ value: '', label: 'Org default' }, ...supervisorOptions]}
                    />
                </View>
            </Card.Content>
        </Card>
    )
}

function MemberInformationCard({
    member,
    control,
    canEditRole,
    watch,
    setValue,
}: {
    member: Member
    control: Control<MemberEditFormData>
    canEditRole: boolean
    watch: UseFormWatch<MemberEditFormData>
    setValue: UseFormSetValue<MemberEditFormData>
}) {
    const theme = useTheme()
    const joinedDate = new Date(member.createdAt).toLocaleDateString()
    const lastActiveDate = formatLastActivity(member.user.last_activity)
    const isInactive = !!member.user.inactive_at

    return (
        <Card style={styles.detailsCard}>
            <Card.Content style={styles.cardContent}>
                <Text variant="titleLarge" style={styles.sectionTitle}>
                    Member Information
                </Text>

                <View style={styles.detailSection}>
                    <View style={styles.detailHeader}>
                        <Mail size={16} color={theme.colors.onSurfaceVariant} />
                        <Text
                            variant="titleSmall"
                            style={[styles.detailLabel, { color: theme.colors.onSurface }]}
                        >
                            Email
                        </Text>
                    </View>
                    <View style={styles.emailEditSection}>
                        <TextInput
                            name="email"
                            control={control}
                            placeholder="Enter email address"
                            keyboardType="email-address"
                            autoCapitalize="none"
                            autoCorrect={false}
                        />
                    </View>
                </View>

                <Divider style={styles.divider} />

                <View style={styles.detailSection}>
                    <View style={styles.detailHeader}>
                        <User size={16} color={theme.colors.onSurfaceVariant} />
                        <Text
                            variant="titleSmall"
                            style={[styles.detailLabel, { color: theme.colors.onSurface }]}
                        >
                            Display Name
                        </Text>
                    </View>
                    <View style={styles.nameEditSection}>
                        <TextInput name="name" control={control} placeholder="Enter display name" />
                    </View>
                </View>

                <Divider style={styles.divider} />

                <View style={styles.detailSection}>
                    <View style={styles.detailHeader}>
                        <Shield size={16} color={theme.colors.onSurfaceVariant} />
                        <Text
                            variant="titleSmall"
                            style={[styles.detailLabel, { color: theme.colors.onSurface }]}
                        >
                            Role
                        </Text>
                    </View>
                    {canEditRole ? (
                        <View style={styles.roleEditSection}>
                            <SelectInput<MemberEditFormData>
                                name="role"
                                horizontal
                                control={control}
                                label=""
                                options={ROLE_OPTIONS.map(opt => ({
                                    label: opt.label,
                                    value: opt.value,
                                }))}
                            />
                        </View>
                    ) : (
                        <Text
                            variant="bodyMedium"
                            style={[
                                styles.detailValue,
                                {
                                    textTransform: 'capitalize',
                                    color: theme.colors.onSurfaceVariant,
                                },
                            ]}
                        >
                            {member.role}
                        </Text>
                    )}
                </View>

                <Divider style={styles.divider} />

                <ColorPickerSection
                    value={watch('color') || ''}
                    onChange={color => setValue('color', color, { shouldDirty: true })}
                />

                <Divider style={styles.divider} />

                <View style={styles.detailSection}>
                    <View style={styles.detailHeader}>
                        <Calendar size={16} color={theme.colors.onSurfaceVariant} />
                        <Text
                            variant="titleSmall"
                            style={[styles.detailLabel, { color: theme.colors.onSurface }]}
                        >
                            Joined
                        </Text>
                    </View>
                    <Text
                        variant="bodyMedium"
                        style={[styles.detailValue, { color: theme.colors.onSurfaceVariant }]}
                    >
                        {joinedDate}, Last Active {lastActiveDate}
                    </Text>
                </View>

                {isInactive && (
                    <>
                        <Divider style={styles.divider} />
                        <View
                            style={[
                                styles.inactiveWarning,
                                { backgroundColor: theme.colors.errorContainer },
                            ]}
                        >
                            <AlertTriangle size={20} color={theme.colors.error} />
                            <View style={styles.inactiveWarningText}>
                                <Text
                                    variant="titleSmall"
                                    style={{ color: theme.colors.error, fontWeight: '600' }}
                                >
                                    Account Inactive
                                </Text>
                                <Text
                                    variant="bodySmall"
                                    style={{ color: theme.colors.onErrorContainer }}
                                >
                                    This user has deleted their account. They cannot sign in.
                                </Text>
                            </View>
                        </View>
                    </>
                )}
            </Card.Content>
        </Card>
    )
}

function getMemberFormDefaults(member?: Member): MemberEditFormData {
    return {
        name: member?.user.name || '',
        email: member?.user.email || '',
        role: (member?.role as UserRole) || 'workforce',
        color: member?.color || '',
        geo_tracking_disabled: member?.user.geo_tracking_disabled ?? false,
        working_hours_override: member?.workingHoursOverride || false,
        work_start_time: member?.workStartTime || DEFAULT_WORKING_HOURS.startTime,
        work_end_time: member?.workEndTime || DEFAULT_WORKING_HOURS.endTime,
        working_days: member?.workingDays || DEFAULT_WORKING_HOURS.workingDays,
        tracking_paused_until: member?.trackingPausedUntil || null,
        supervisor: member?.supervisor || '',
        clockin_reminders_disabled: member?.clockinRemindersDisabled ?? false,
    }
}

function useAdminActions(member: Member | undefined) {
    const [usersCollection, userOrgCollection] = useStore('users', 'user_org')
    const [isReactivating, setIsReactivating] = useState(false)
    const [isSendingReset, setIsSendingReset] = useState(false)
    const [isTogglingOrgActive, setIsTogglingOrgActive] = useState(false)

    const handleToggleOrgActive = async () => {
        if (!member) return
        setIsTogglingOrgActive(true)
        try {
            const isCurrentlyInactive = !!member.inactiveAt
            await performMutations(function* () {
                yield userOrgCollection.update(member.userOrgId, draft => {
                    const extendedDraft = draft as typeof draft & { inactive_at?: string }
                    extendedDraft.inactive_at = isCurrentlyInactive ? '' : new Date().toISOString()
                })
            })
            notify.success(
                isCurrentlyInactive
                    ? 'User enabled in organization'
                    : 'User disabled in organization'
            )
        } catch (error) {
            captureException('Toggle org active', error)
            notify.error(errorToString(error, 'Failed to update user status'))
        } finally {
            setIsTogglingOrgActive(false)
        }
    }

    const handleReactivate = async () => {
        if (!member) return
        setIsReactivating(true)
        try {
            const response = await fetch(`${PB_SERVER_ADDR}/api/admin/reactivate-user`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${pb.authStore.token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ userId: member.user.id }),
            })
            const data = await response.json()
            if (!response.ok) {
                throw new Error(data.message || 'Failed to reactivate user')
            }
            notify.success('User reactivated successfully')
        } catch (error) {
            captureException('Reactivate user', error)
            notify.error(errorToString(error, 'Failed to reactivate user'))
        } finally {
            setIsReactivating(false)
        }
    }

    const handleSendPasswordReset = async () => {
        if (!member) return
        setIsSendingReset(true)
        try {
            const response = await fetch(`${PB_SERVER_ADDR}/api/admin/trigger-password-reset`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${pb.authStore.token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ userId: member.user.id }),
            })
            const data = await response.json()
            if (!response.ok) {
                throw new Error(data.message || 'Failed to send password reset')
            }
            notify.success('Password reset email sent')
        } catch (error) {
            captureException('Send password reset', error)
            notify.error(errorToString(error, 'Failed to send password reset'))
        } finally {
            setIsSendingReset(false)
        }
    }

    return {
        usersCollection,
        userOrgCollection,
        isReactivating,
        isSendingReset,
        isTogglingOrgActive,
        handleToggleOrgActive,
        handleReactivate,
        handleSendPasswordReset,
    }
}

export default function MemberDetailPage() {
    const params = useLocalSearchParams<{ orgSlug: string; memberId: string }>()
    const { navigateBackTo } = useNavigateBack()
    const semanticColors = useSemanticColors()
    const { members, isLoading } = useMembers()
    const { canUpdateMemberRole } = useMemberPermissions()
    const { user: currentUser } = useAuth()
    const { role } = useCurrentOrg()
    const isAdmin = role === 'admin'
    const isNotWorkforce = role !== 'workforce'

    const member = useMemo(
        () => members.members.find(m => m.id === params.memberId),
        [members.members, params.memberId]
    )

    const {
        usersCollection,
        userOrgCollection,
        isReactivating,
        isSendingReset,
        isTogglingOrgActive,
        handleToggleOrgActive,
        handleReactivate,
        handleSendPasswordReset,
    } = useAdminActions(member)

    const {
        control,
        handleSubmit,
        setError,
        getValues,
        reset,
        watch,
        setValue,
        formState: { isDirty },
    } = useForm<MemberEditFormData>({
        resolver: zodResolver(memberEditSchema),
        defaultValues: getMemberFormDefaults(member),
        mode: 'onChange',
    })

    const updateMember = useMutation({
        mutationFn: async (data: MemberEditFormData) => {
            if (!member) throw new Error('Member not found')

            return await performMutations(function* () {
                yield usersCollection.update(member.user.id, draft => {
                    draft.name = data.name
                    draft.email = data.email
                    draft.geo_tracking_disabled = data.geo_tracking_disabled
                })
                yield userOrgCollection.update(member.userOrgId, draft => {
                    const extendedDraft = draft as typeof draft & {
                        working_hours_override?: boolean
                        work_start_time?: string
                        work_end_time?: string
                        working_days?: number[] | null
                        color?: string
                        tracking_paused_until?: string
                        supervisor?: string
                        clockin_reminders_disabled?: boolean
                    }
                    extendedDraft.role = data.role
                    extendedDraft.color = data.color || ''
                    extendedDraft.working_hours_override = data.working_hours_override
                    extendedDraft.work_start_time = data.working_hours_override
                        ? data.work_start_time
                        : ''
                    extendedDraft.work_end_time = data.working_hours_override
                        ? data.work_end_time
                        : ''
                    extendedDraft.working_days = data.working_hours_override
                        ? data.working_days
                        : null
                    extendedDraft.tracking_paused_until = data.tracking_paused_until || ''
                    extendedDraft.supervisor = data.supervisor || ''
                    extendedDraft.clockin_reminders_disabled = data.clockin_reminders_disabled
                })
            })
        },
        onError: handleMutationErrorsWithForm({ setError, getValues }),
        onSuccess: () => {
            notify.success('User updated successfully')
            navigateBackTo(`/${params.orgSlug}/settings/team`)
        },
    })

    useEffect(() => {
        if (member) {
            reset(getMemberFormDefaults(member))
        }
    }, [member, reset])

    useHeaderOptions({
        buttons: {
            type: 'save',
            onPress: handleSubmit(data => updateMember.mutate(data)),
            loading: updateMember.isPending,
            loadingTitle: 'Saving...',
        },
        title: 'Save',
    })

    if (isLoading) {
        return <LoadingIndicator />
    }

    if (!member) {
        return (
            <Page>
                <Card
                    style={[
                        styles.errorCard,
                        {
                            backgroundColor: semanticColors.errorLight,
                            borderColor: semanticColors.errorBorder,
                        },
                    ]}
                >
                    <Card.Content>
                        <Text style={{ color: semanticColors.errorText }}>
                            Member not found. They may have been removed or you don't have
                            permission to view them.
                        </Text>
                    </Card.Content>
                </Card>
            </Page>
        )
    }

    const canEditRole = canUpdateMemberRole(member)
    const isInactive = !!member.user.inactive_at
    const isOrgInactive = !!member.inactiveAt
    const isSelf = currentUser?.id === member.user.id

    return (
        <Page>
            <MemberInformationCard
                member={member}
                control={control}
                canEditRole={canEditRole}
                watch={watch}
                setValue={setValue}
            />

            <LocationTrackingCard
                control={control}
                watch={watch}
                setValue={setValue}
                canEdit={isNotWorkforce}
                isPending={updateMember.isPending}
            />

            <SupervisorCard
                isVisible={isAdmin}
                control={control}
                members={members}
                watch={watch}
                setValue={setValue}
            />

            <AdminActionsCard
                isVisible={isAdmin && !isSelf}
                isInactive={isInactive}
                isOrgInactive={isOrgInactive}
                onReactivate={handleReactivate}
                onSendPasswordReset={handleSendPasswordReset}
                onToggleOrgActive={handleToggleOrgActive}
                isReactivating={isReactivating}
                isSendingReset={isSendingReset}
                isTogglingActive={isTogglingOrgActive}
                inactiveAt={member.inactiveAt}
            />
            <UnsavedChangesDialog isDirty={isDirty} />
        </Page>
    )
}

const styles = StyleSheet.create({
    saveButton: {
        alignSelf: 'flex-start',
    },
    errorCard: {
        borderWidth: 1,
    },
    detailsCard: {
        borderWidth: 1,
    },
    cardContent: {
        gap: 16,
    },
    sectionTitle: {
        fontWeight: '600',
    },
    detailSection: {
        gap: 8,
    },
    detailHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    detailLabel: {
        fontWeight: '600',
    },
    detailValue: {
        paddingLeft: 24,
    },
    divider: {
        marginVertical: 4,
    },
    roleEditSection: {
        paddingLeft: 24,
    },
    nameEditSection: {
        paddingLeft: 24,
    },
    emailEditSection: {
        paddingLeft: 24,
    },
    inactiveWarning: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        padding: 12,
        borderRadius: 8,
    },
    inactiveWarningText: {
        flex: 1,
        gap: 2,
    },
    locationCard: {
        borderWidth: 1,
    },
    sectionHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    toggleRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 16,
    },
    toggleInfo: {
        flex: 1,
        gap: 2,
    },
    actionsCard: {
        borderWidth: 1,
    },
    actionItem: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
    },
    actionInfo: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        flex: 1,
    },
    actionText: {
        flex: 1,
        gap: 2,
    },
    colorSwatchGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'space-around',
        paddingLeft: 24,
        rowGap: 10,
    },
    colorSwatch: {
        width: 36,
        height: 36,
        borderRadius: 18,
    },
})
